import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Avatar, AvatarPicker } from '../components/Avatar';
import ConfirmDialog from '../components/ConfirmDialog';
import { ThemeToggle } from '../contexts/ThemeContext';
import { setActiveProfile, ensureProfileAxiosInterceptor } from '../utils/profile';
import { DEFAULT_AVATAR_ID } from '../data/avatars';
import './select-profile.css';

ensureProfileAxiosInterceptor();

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('adeeb-token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function SelectProfile() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [cap, setCap] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', avatar: DEFAULT_AVATAR_ID });
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Confirmation modal — replaces window.confirm() for delete.
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    try {
      if (!localStorage.getItem('adeeb-token')) {
        navigate('/login?next=/profiles/select', { replace: true });
        return;
      }
      const { data } = await axios.get('/api/profiles', { headers: authHeaders() });
      setProfiles(data.profiles || []);
      setCap(data.cap || 3);
      // First-run UX: if a logged-in user has no profiles, drop them
      // straight into the create form so they don't see an empty page.
      if ((data.profiles || []).length === 0) {
        setCreating(true);
        setDraft({ name: '', avatar: DEFAULT_AVATAR_ID });
      }
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load profiles.');
    } finally {
      setLoading(false);
    }
  };

  // load is stable enough — depending on it would re-run on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const choose = async (profile) => {
    setActiveProfile(profile);
    // Touch — best-effort timestamp for the parent dashboard.
    axios.post(`/api/profiles/${profile._id}/touch`, null, { headers: authHeaders() }).catch(() => {});
    navigate('/', { replace: true });
  };

  const startEdit = (profile) => {
    setEditingId(profile._id);
    setDraft({ name: profile.name, avatar: profile.avatar || DEFAULT_AVATAR_ID });
    setCreating(false);
  };
  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setDraft({ name: '', avatar: DEFAULT_AVATAR_ID });
  };
  const cancel = () => {
    setEditingId(null);
    setCreating(false);
    setError('');
  };

  const save = async () => {
    if (busy) return;
    const name = draft.name.trim();
    if (!name) { setError('Please give the profile a name.'); return; }
    setBusy(true); setError('');
    try {
      if (creating) {
        await axios.post('/api/profiles', { name, avatar: draft.avatar }, { headers: authHeaders() });
      } else if (editingId) {
        await axios.patch(`/api/profiles/${editingId}`, { name, avatar: draft.avatar }, { headers: authHeaders() });
      }
      setCreating(false); setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save profile.');
    } finally {
      setBusy(false);
    }
  };

  const requestRemove = (profile) => {
    if (profiles.length <= 1) {
      setError('You must keep at least one profile.');
      return;
    }
    setPendingDelete(profile);
  };

  const confirmRemove = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/profiles/${pendingDelete._id}`, { headers: authHeaders() });
      setPendingDelete(null);
      // If the user was editing this profile, drop out of the form.
      if (editingId === pendingDelete._id) { setEditingId(null); setCreating(false); }
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not delete profile.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="sp-page">
        <div className="sp-loading"><div className="sp-spinner" /><p>Loading profiles…</p></div>
      </div>
    );
  }

  const showForm = creating || editingId;

  return (
    <div className="sp-page">
      <div className="sp-shell">
        <header className="sp-head">
          <div className="sp-eyebrow">DeenLit</div>
          <h1 className="sp-title">{showForm ? (creating ? 'Add a profile' : 'Edit profile') : 'Who’s reading?'}</h1>
          {!showForm && (
            <p className="sp-sub">Pick a profile to continue, or add a new one for another reader on this account.</p>
          )}
        </header>

        {error && <div className="sp-error">{error}</div>}

        {showForm ? (
          <div className="sp-form">
            <button
              type="button"
              className="sp-form-avatar-btn"
              onClick={() => setAvatarPickerOpen(true)}
              aria-label="Choose avatar"
            >
              <Avatar value={draft.avatar} name={draft.name || '?'} size={132} />
              <span className="sp-form-avatar-edit">Change</span>
            </button>
            <input
              className="sp-form-name"
              type="text"
              value={draft.name}
              maxLength={60}
              autoFocus
              placeholder="Profile name"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
            />
            <div className="sp-form-actions">
              <button type="button" className="sp-btn sp-btn-primary" disabled={busy || !draft.name.trim()} onClick={save}>
                {busy ? 'Saving…' : creating ? 'Create profile' : 'Save changes'}
              </button>
              <button type="button" className="sp-btn sp-btn-ghost" onClick={cancel} disabled={busy}>Cancel</button>
            </div>

            {editingId && profiles.length > 1 && (
              <div className="sp-danger-zone">
                <button
                  type="button"
                  className="sp-btn sp-btn-danger"
                  onClick={() => {
                    const target = profiles.find((p) => p._id === editingId);
                    if (target) requestRemove(target);
                  }}
                  disabled={busy}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 6h18" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <line x1="10" y1="11" x2="10" y2="17" />
                    <line x1="14" y1="11" x2="14" y2="17" />
                  </svg>
                  <span>Delete this profile</span>
                </button>
              </div>
            )}
            <AvatarPicker
              open={avatarPickerOpen}
              currentValue={draft.avatar}
              onClose={() => setAvatarPickerOpen(false)}
              onPick={(id) => { setDraft((d) => ({ ...d, avatar: id })); setAvatarPickerOpen(false); }}
            />
          </div>
        ) : (
          <>
            <ul className="sp-grid">
              {profiles.map((p) => (
                <li key={p._id} className="sp-tile">
                  <button type="button" className="sp-tile-btn" onClick={() => choose(p)} aria-label={`Continue as ${p.name}`}>
                    <Avatar value={p.avatar} name={p.name} size={132} />
                    <div className="sp-tile-name">{p.name}</div>
                  </button>
                  <button type="button" className="sp-tile-edit" onClick={() => startEdit(p)} aria-label={`Edit ${p.name}`}>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M11 5h-7v15h15v-7" />
                      <path d="M18.5 2.5l3 3-9.5 9.5h-3v-3z" />
                    </svg>
                  </button>
                </li>
              ))}
              {profiles.length < cap && (
                <li className="sp-tile sp-tile--add">
                  <button type="button" className="sp-tile-btn sp-tile-btn--add" onClick={startCreate} aria-label="Add a profile">
                    <span className="sp-tile-add-circle">+</span>
                    <div className="sp-tile-name">Add profile</div>
                  </button>
                </li>
              )}
            </ul>
            <div className="sp-cap-note">
              <span>{profiles.length} of {cap} profiles used.</span>
              {cap < 6 && (
                <p className="sp-upsell">
                  Need more readers in the family?{' '}
                  <Link to="/settings" className="sp-upsell-link">
                    Upgrade to Family for up to 6 profiles →
                  </Link>
                </p>
              )}
            </div>

            <div className="sp-manage-bar">
              <ThemeToggle className="theme-toggle--inline sp-theme-toggle" />
          <span className="page-tour-host" />
              <Link
                to="/settings"
                className="sp-manage-btn"
                aria-label="Account settings"
                title="Account settings"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                <span>Account settings</span>
              </Link>
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={pendingDelete ? `Delete ${pendingDelete.name}?` : ''}
        body="Their reading progress, favourites, playlist, quiz scores and rewards will be permanently removed. This can't be undone."
        confirmLabel="Delete profile"
        cancelLabel="Keep profile"
        busy={deleting}
        onClose={() => { if (!deleting) setPendingDelete(null); }}
        onConfirm={confirmRemove}
      />
    </div>
  );
}
